import { AlertTriangle, Info } from 'lucide-react'
import { dinero } from './formato'

/*
 * Los bloques de DINERO de la ficha: IVA, prorrateo, cobro del link e impacto antes/después.
 * Los comparten el paquete vigente y el editor de addenda.
 */

/**
 * El prorrateo del ciclo en curso, con cifra o con el motivo de que no la haya.
 *
 * El caso `sin_suscripcion` no es un error: una empresa demo o en prueba no tiene suscripción en la
 * pasarela, así que el abono anunciado no va a existir. Decirlo evita que el asesor le prometa al
 * cliente una devolución que nadie va a hacer.
 */
export function BloqueProrrateo({ p, moneda, cargoNeto }) {
  if (!p) return null

  if (!p.aplica) {
    if (p.motivo === 'sin_diferencia') return null
    if (p.motivo === 'cobro_por_enlace') {
      return (
        <p className="text-caption text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
          <Info size={13} className="mt-0.5 shrink-0" />
          {p.sentido === 'cobro'
            ? 'Se cobra con link de Wompi: la diferencia del mes completo más los cargos únicos. Desde la próxima renovación se cobra el precio nuevo.'
            : 'Se cobra con link de Wompi, que no hace abonos: el precio nuevo rige desde la próxima renovación.'}
        </p>
      )
    }
    return (
      <p className="text-caption text-neutral-500 dark:text-neutral-400 flex items-start gap-1.5">
        <Info size={13} className="mt-0.5 shrink-0" />
        Sin prorrateo: esta empresa no tiene suscripción de cobro activa (demo o facturación fuera de
        la pasarela). El paquete cambia igual, pero no se genera ni abono ni cargo.
      </p>
    )
  }

  const abono = p.sentido === 'abono'
  return (
    <div className={`rounded-md border px-3 py-2 ${abono ? 'border-success-200 dark:border-success-800 bg-success-50/60 dark:bg-success/15' : 'border-warning-200 dark:border-warning-800 bg-warning-50/60 dark:bg-warning/15'}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-caption font-medium text-neutral-700 dark:text-neutral-300">
          {abono ? 'Abono por el ciclo no consumido' : 'Prorrateo del ciclo en curso'}
        </span>
        <span className={`text-body-sm font-semibold tabular-nums ${abono ? 'text-success-700 dark:text-success-300' : 'text-neutral-900 dark:text-neutral-50'}`}>
          {p.monto == null ? '—' : `${abono ? '−' : '+'}${dinero(p.monto, moneda)}`}
        </span>
      </div>
      <p className="text-micro text-neutral-500 dark:text-neutral-400 mt-1">
        {p.monto == null
          ? 'Lo calculará la pasarela al aplicar el cambio: no conocemos las fechas del ciclo para estimarlo aquí.'
          : `Estimado sobre ${p.diasRestantes} de ${p.diasCiclo} días sin consumir. La cifra exacta la calcula la pasarela al aplicar.`}
      </p>
      {/* El checkout de una ampliación cobra el delta mensual COMPLETO, no la parte proporcional.
          Al principio del ciclo coincide; a mitad de ciclo el cliente paga de más. Se avisa en vez
          de ocultarlo detrás de la palabra "prorrateo". */}
      {!abono && p.monto != null && cargoNeto > p.monto && (
        <p className="text-micro text-warning-700 dark:text-warning-300 mt-1">
          Ojo: el link de pago cobrará {dinero(cargoNeto, moneda)} (el mes completo de diferencia más
          los cargos únicos), no la parte proporcional.
        </p>
      )}
    </div>
  )
}

/** «neto + IVA = total» en una línea, para que el asesor diga el monto final sin hacer cuentas. */
export function LineaIva({ c, moneda, sufijo = '' }) {
  if (!c) return null
  if (c.error) {
    return <span className="block text-caption text-danger-700 dark:text-danger-300">No se puede calcular el IVA: {c.error}</span>
  }
  const m = c.mensual
  if (!m) return null
  return (
    <span className="block text-caption text-neutral-500 dark:text-neutral-400">
      {c.ivaPct != null
        ? <>+ IVA {c.ivaPct} % {dinero(m.iva, moneda)} = <span className="font-semibold text-neutral-800 dark:text-neutral-200">{dinero(m.total, moneda)}{sufijo}</span> con IVA</>
        : 'Sin IVA (cobro internacional por Stripe)'}
    </span>
  )
}

function FilaCobro({ label, v, moneda, fuerte }) {
  return (
    <div className={`flex items-center justify-between gap-2 px-3 py-1.5 ${fuerte ? 'bg-surface-muted' : ''}`}>
      <span className={`text-caption ${fuerte ? 'font-medium text-neutral-600 dark:text-neutral-400' : 'text-neutral-700 dark:text-neutral-300'}`}>{label}</span>
      <span className={`tabular-nums ${fuerte ? 'text-body-sm font-semibold text-neutral-900 dark:text-neutral-50' : 'text-caption text-neutral-800 dark:text-neutral-200'}`}>{dinero(v, moneda)}</span>
    </div>
  )
}

/**
 * Lo que paga el cliente, SIEMPRE con el impuesto a la vista: la mensualidad del paquete nuevo y,
 * si hay ampliación, lo que cobra el link. Con Wompi (El Salvador) el IVA del país va encima del
 * neto, igual que en el alta: sin este bloque el asesor cotizaba $X y el cliente veía $X + IVA.
 */
export function BloqueCobro({ c, moneda }) {
  if (!c) return null
  if (c.error) {
    return (
      <p className="text-caption text-danger-700 dark:text-danger-300 flex items-start gap-1.5">
        <AlertTriangle size={13} className="mt-0.5 shrink-0" /> No se puede calcular el IVA del cobro: {c.error}
      </p>
    )
  }
  const iva = c.ivaPct != null ? `IVA ${c.ivaPct} %` : null
  return (
    <div className="space-y-2">
      {c.mensual && (
        <div className="rounded-md border border-line bg-card divide-y divide-neutral-100 dark:divide-neutral-800">
          <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase px-3 pt-2 pb-1">Mensualidad que paga el cliente</p>
          <FilaCobro label="Neto" v={c.mensual.neto} moneda={moneda} />
          {iva && <FilaCobro label={iva} v={c.mensual.iva} moneda={moneda} />}
          <FilaCobro label={iva ? 'Total al mes con IVA' : 'Total al mes (sin IVA: cobro internacional)'} v={c.mensual.total} moneda={moneda} fuerte />
        </div>
      )}
      {c.link && (
        <div className="rounded-md border border-warning-200 dark:border-warning-800 bg-card divide-y divide-neutral-100 dark:divide-neutral-800">
          <p className="text-micro text-neutral-500 dark:text-neutral-400 uppercase px-3 pt-2 pb-1">
            Cobro del link de pago{c.proveedor === 'wompi' ? ' (Wompi)' : ' (Stripe)'}
          </p>
          <FilaCobro label="Neto" v={c.link.neto} moneda={moneda} />
          {iva && <FilaCobro label={iva} v={c.link.iva} moneda={moneda} />}
          <FilaCobro label="Total a pagar hoy" v={c.link.total} moneda={moneda} fuerte />
        </div>
      )}
    </div>
  )
}

export function FilaImpacto({ label, antes, despues, moneda }) {
  const sube = despues > antes
  const baja = despues < antes
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
      <span className="text-body-sm text-neutral-600 dark:text-neutral-400">{label}</span>
      <span className="text-body-sm flex flex-wrap items-center gap-x-2 tabular-nums">
        <span className="text-neutral-400 dark:text-neutral-500 line-through">{dinero(antes, moneda)}</span>
        <span className={`font-medium ${sube ? 'text-danger-700 dark:text-danger-300' : baja ? 'text-success-700 dark:text-success-300' : 'text-neutral-800 dark:text-neutral-200'}`}>
          {dinero(despues, moneda)}
        </span>
      </span>
    </div>
  )
}
